import { getDocumentBlob } from './wordDraft';
import { SIGNUS_API_URL } from './constants';
import { ApplyOperation } from './analysis';
import { ApiError, extractErrorMessage, extractErrorType, fetchWithTokenRefresh } from './apiFetch';

export interface DetectedParty {
  recipient: string;
  party: string;
}

export interface RedlineSuggestionResult {
  title: string;
  rewrite?: string;
  rationale: string;
  operation: ApplyOperation;
}

/**
 * Only these ids have a signus-service RedlineEditType counterpart — this is
 * exactly the Redlining tab's palette (REDLINING_EDIT_TYPE_IDS) plus 'custom'.
 */
const EDIT_TYPE_ID_TO_BACKEND: Record<string, string> = {
  shorten: 'SHORTEN',
  simplify: 'SIMPLIFY',
  soften_tone: 'SOFTEN',
  add_specificity: 'ADD_SPECIFICITY',
  favor_my_side: 'FAVOR_MY_SIDE',
  neutralize: 'NEUTRALIZE',
  custom: 'CUSTOM_INSTRUCTION',
};

function toBackendEditType(editTypeId: string): string {
  const backendType = EDIT_TYPE_ID_TO_BACKEND[editTypeId];
  if (!backendType) {
    throw new Error(`Edit type "${editTypeId}" isn't supported by signus-service redline yet.`);
  }
  return backendType;
}

function toApplyOperation(operation: string): ApplyOperation {
  if (operation === 'INSERT_BEFORE') return 'insert_before';
  if (operation === 'INSERT_AFTER') return 'insert_after';
  return 'replace';
}

function parseSuggestionResult(json: unknown): RedlineSuggestionResult {
  const payload = ((json as { payload?: unknown })?.payload ?? json) as {
    title: string;
    rewrite?: string;
    rationale: string;
    operation: string;
  };
  return {
    title: payload.title,
    rewrite: payload.rewrite,
    rationale: payload.rationale,
    operation: toApplyOperation(payload.operation),
  };
}

/**
 * Uploads the current document to start a redline session on the backend.
 * The session's document is what party detection (and, later, rewrite) act on.
 */
export async function createRedlineSession(teamSlug: string, accessToken: string): Promise<string> {
  const blob = await getDocumentBlob();

  const res = await fetchWithTokenRefresh(
    `${SIGNUS_API_URL}/v0/word/teams/${encodeURIComponent(teamSlug)}/redline/sessions`,
    accessToken,
    token => {
      const form = new FormData();
      form.append('file', blob, 'document.docx');
      return { method: 'POST', headers: { Authorization: token }, body: form };
    },
  );

  const json = await res.json();
  if (!res.ok) {
    throw new ApiError(
      extractErrorMessage(json, `Redline session creation failed (${res.status})`),
      extractErrorType(json),
    );
  }

  const payload = (json.payload ?? json) as Record<string, unknown>;
  return payload.sessionId as string;
}

export async function detectParties(
  teamSlug: string,
  accessToken: string,
  sessionId: string,
): Promise<DetectedParty[]> {
  const res = await fetchWithTokenRefresh(
    `${SIGNUS_API_URL}/v0/word/teams/${encodeURIComponent(teamSlug)}/redline/sessions/${encodeURIComponent(sessionId)}/detect-parties`,
    accessToken,
    token => ({ method: 'POST', headers: { Authorization: token } }),
  );

  const json = await res.json();
  if (!res.ok) {
    throw new ApiError(
      extractErrorMessage(json, `Party detection failed (${res.status})`),
      extractErrorType(json),
    );
  }

  const payload = (json.payload ?? json) as { parties?: DetectedParty[] };
  return payload.parties ?? [];
}

export async function triggerRewrite(
  teamSlug: string,
  accessToken: string,
  sessionId: string,
  opts: { editTypeId: string; clauseText: string; customInstruction?: string; userParty?: string },
): Promise<RedlineSuggestionResult> {
  const res = await fetchWithTokenRefresh(
    `${SIGNUS_API_URL}/v0/word/teams/${encodeURIComponent(teamSlug)}/redline/sessions/${encodeURIComponent(sessionId)}/rewrite`,
    accessToken,
    token => ({
      method: 'POST',
      headers: { Authorization: token, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        editType: toBackendEditType(opts.editTypeId),
        clauseText: opts.clauseText,
        customInstruction: opts.customInstruction,
        userParty: opts.userParty,
      }),
    }),
  );

  const json = await res.json();
  if (!res.ok) {
    throw new ApiError(
      extractErrorMessage(json, `Redline rewrite failed (${res.status})`),
      extractErrorType(json),
    );
  }
  return parseSuggestionResult(json);
}

export async function refineRewrite(
  teamSlug: string,
  accessToken: string,
  sessionId: string,
  opts: { editTypeId: string },
): Promise<RedlineSuggestionResult> {
  const res = await fetchWithTokenRefresh(
    `${SIGNUS_API_URL}/v0/word/teams/${encodeURIComponent(teamSlug)}/redline/sessions/${encodeURIComponent(sessionId)}/refine`,
    accessToken,
    token => ({
      method: 'POST',
      headers: { Authorization: token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ editType: toBackendEditType(opts.editTypeId) }),
    }),
  );

  const json = await res.json();
  if (!res.ok) {
    throw new ApiError(
      extractErrorMessage(json, `Redline refine failed (${res.status})`),
      extractErrorType(json),
    );
  }
  return parseSuggestionResult(json);
}
